import { Node, mergeAttributes } from '@tiptap/core';
import { TextSelection } from '@tiptap/pm/state';
import { Plugin, PluginKey } from 'prosemirror-state';
import { syncIndentOnDom } from '../lib/post_block_indent.js';
import { createCaptionDOM, setupCaptionKeyboardHandling } from './CaptionExtension';
import {
  POST_EDITOR_FLOATING_MENU_KINDS,
  postEditorApplyFloatingToolbarElement,
  postEditorFloatingToolbarGroupClasses,
  postEditorVideoFixedMenuClasses,
} from '../lib/post_editor_constants.js';

export const VideoExtension = Node.create({
  name: 'video',
  group: 'block',
  selectable: true,
  draggable: true,
  atom: false,
  content: 'inline*',
  
  addAttributes() {
    return {
      src: { default: null },
      poster: { default: null },
      controls: { default: true },
      style: { default: 'max-width:100%' },
      resourceId: { default: null },
    };
  },
  
  parseHTML() {
    return [
      // Primary rule: figure with data-tiptap-video attribute (highest priority)
      {
        tag: 'figure[data-tiptap-video]',
        priority: 100,
        getAttrs: dom => {
          const video = dom.querySelector('video');
          if (!video) return false;
          return {
            src: video?.getAttribute('src'),
            poster: video?.getAttribute('poster') || null,
            controls: video?.hasAttribute('controls'),
            style: video?.getAttribute('style'),
            resourceId: dom.getAttribute('data-resource-id') || null,
          };
        }
      },
      // Legacy support: parse standalone video tags and convert them to figure format
      {
        tag: 'video[src]',
        priority: 50,
        getAttrs: dom => {
          // Only parse if this video is NOT inside a figure (avoid double parsing)
          if (dom.closest('figure')) return false;
          return {
            src: dom.getAttribute('src'),
            poster: dom.getAttribute('poster') || null,
            controls: dom.hasAttribute('controls'),
            style: dom.getAttribute('style'),
            resourceId: dom.getAttribute('data-resource-id') || dom.closest('figure')?.getAttribute('data-resource-id') || null,
          };
        },
      },
    ];
  },
  
  renderHTML({ HTMLAttributes }) {
    const { resourceId, indent, 'data-ps-indent': dataPsIndent, ...videoAttrs } = HTMLAttributes;
    const videoNode = ['video', mergeAttributes(videoAttrs, {
      class: 'post-content-video-vid',
      controls: true,
    })];
    
    const figureAttrs = {
      'data-tiptap-video': '1',
      class: 'post-content-video',
    };
    if (resourceId) {
      figureAttrs['data-resource-id'] = resourceId.toString();
    }
    const indentLevel = dataPsIndent || (typeof indent === 'number' && indent > 0 ? String(indent) : null);
    if (indentLevel) {
      figureAttrs['data-ps-indent'] = String(indentLevel);
    }
    
    // Render the node with a figcaption that will contain the node's inline content
    return [
      'figure', figureAttrs,
      videoNode,
      ['figcaption', {
        class: 'video-caption-container ps-post-style-caption'
      }, 0]
    ];
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('videoCaptionEnterHandler'),
        props: {
          handleKeyDown: (view, event) => {
            // Handle backspace in empty paragraph after video
            if (event.key === 'Backspace') {
              const { state } = view;
              const { selection } = state;
              const { $from } = selection;
              
              // Check if we're in an empty paragraph
              const currentNode = $from.node($from.depth);
              
              if (currentNode.type.name !== 'paragraph') {
                return false;
              }
              
              const isEmpty = currentNode.content.size === 0 || 
                             (currentNode.content.size <= 2 && currentNode.textContent.trim() === '');
              
              if (!isEmpty) {
                return false;
              }
              
              // Check if we're at the start of the paragraph
              const isAtStart = $from.parentOffset === 0 || 
                               ($from.pos === $from.start($from.depth) && currentNode.content.size === 0);
              
              if (!isAtStart) {
                return false;
              }
              
              // Find the paragraph position
              const paragraphPos = $from.before($from.depth);
              
              if (paragraphPos <= 0) {
                return false;
              }
              
              // Check if we're inside a list
              let isInList = false;
              let listItemNode = null;
              let listItemPos = null;
              for (let depth = $from.depth; depth >= 0; depth--) {
                const nodeAtDepth = $from.node(depth);
                if (nodeAtDepth.type.name === 'listItem') {
                  isInList = true;
                  listItemNode = nodeAtDepth;
                  // Use before() to get the position before the listItem node
                  listItemPos = depth === 0 ? 0 : $from.before(depth);
                  break;
                }
              }
              
              let foundVideo = null;
              let foundVideoPos = null;
              
              if (isInList && listItemNode && listItemPos !== null) {
                // Inside list: check siblings within the same listItem
                // Use the listItemNode we already found (from $from.node(depth))
                // Find paragraph index within listItem
                let paragraphIndex = -1;
                const listItemStart = listItemPos + 1; // Content starts after the listItem node
                let currentPos = listItemStart;
                
                // paragraphPos is before the paragraph node, so the paragraph starts at paragraphPos + 1
                const paragraphStartPos = paragraphPos + 1;
                for (let i = 0; i < listItemNode.content.size; i++) {
                  const child = listItemNode.content.child(i);
                  // Only match if it's actually a paragraph and the position matches
                  if (child.type.name === 'paragraph' && (currentPos === paragraphStartPos || Math.abs(currentPos - paragraphStartPos) < 5)) {
                    paragraphIndex = i;
                    break;
                  }
                  currentPos += child.nodeSize;
                }
                
                // Check previous siblings in the listItem
                if (paragraphIndex > 0) {
                  for (let i = paragraphIndex - 1; i >= 0; i--) {
                    const sibling = listItemNode.content.child(i);
                    
                    if (sibling.type.name === 'video') {
                      // Calculate position
                      let siblingPos = listItemStart;
                      for (let j = 0; j < i; j++) {
                        siblingPos += listItemNode.content.child(j).nodeSize;
                      }
                      foundVideo = sibling;
                      foundVideoPos = siblingPos;
                      break;
                    }
                    
                    if (sibling.isBlock) {
                      break;
                    }
                  }
                }
              } else {
                // Not in list: check if the node immediately before the paragraph is a video
                let prevBlockPos = paragraphPos - 1;
                while (prevBlockPos >= 0) {
                  const nodeAtPos = state.doc.nodeAt(prevBlockPos);
                  if (!nodeAtPos) break;
                  
                  // If we find a video node, handle backspace
                  if (nodeAtPos.type.name === 'video') {
                    foundVideo = nodeAtPos;
                    foundVideoPos = prevBlockPos;
                    break;
                  }
                  
                  // If it's a block node but not a video, stop
                  if (nodeAtPos.isBlock) break;
                  
                  // Move to the start of this node
                  prevBlockPos = prevBlockPos - nodeAtPos.nodeSize;
                }
              }
              
              if (foundVideo && foundVideoPos !== null) {
                // Prevent default behavior
                event.preventDefault();
                event.stopPropagation();
                
                // Delete the empty paragraph
                const tr = state.tr;
                tr.delete(paragraphPos, paragraphPos + currentNode.nodeSize);
                
                // Move cursor to the video caption area
                const videoNode = tr.doc.nodeAt(foundVideoPos);
                if (videoNode) {
                  // Position inside the video's caption (inline content)
                  const captionPos = foundVideoPos + 1;
                  try {
                    const captionResolved = tr.doc.resolve(captionPos);
                    if (captionResolved) {
                      tr.setSelection(TextSelection.near(captionResolved));
                    }
                  } catch (e) {
                    // Fallback: set selection at video position
                    const videoResolved = tr.doc.resolve(foundVideoPos);
                    if (videoResolved) {
                      tr.setSelection(TextSelection.near(videoResolved));
                    }
                  }
                }
                
                tr.scrollIntoView();
                view.dispatch(tr);
                
                return true;
              }
              
              return false;
            }
            
            if (event.key !== 'Enter') return false;
            
            const { state } = view;
            const { selection } = state;
            const { $from } = selection;
            
            // Check if we're inside a video node
            let videoNode = null;
            let videoPos = null;
            let videoDepth = -1;
            
            for (let depth = $from.depth; depth >= 0; depth--) {
              const nodeAtDepth = $from.node(depth);
              if (nodeAtDepth.type.name === 'video') {
                videoNode = nodeAtDepth;
                videoPos = $from.start(depth);
                videoDepth = depth;
                break;
              }
            }
            
            if (!videoNode || videoPos === null) return false;
            
            // Check if we're in the caption (inline content of video)
            const isInCaption = $from.depth === videoDepth || 
                              ($from.depth > videoDepth && $from.parent.type === videoNode.type);
            
            if (!isInCaption) return false;
            
            // If Shift+Enter, allow default behavior (inserts hardBreak)
            // Use explicit check to handle cases where shiftKey might be undefined
            if (event.shiftKey === true || event.getModifierState?.('Shift') === true) {
              return false;
            }
            
            // Check if caption is empty or at end
            const captionContent = videoNode.content;
            const captionText = captionContent.textContent || '';
            const isAtEnd = $from.pos >= videoPos + videoNode.nodeSize - 1;
            const isEmpty = captionContent.size === 0 || 
                           (captionContent.size <= 2 && captionText.trim() === '');
            
            if (!isAtEnd && !isEmpty) return false;
            
            // Find list item
            let listItemPos = null;
            let listItemNode = null;
            
            const videoResolved = state.doc.resolve(videoPos);
            for (let depth = videoResolved.depth; depth >= 0; depth--) {
              const nodeAtDepth = videoResolved.node(depth);
              if (nodeAtDepth.type.name === 'listItem') {
                listItemPos = videoResolved.start(depth);
                listItemNode = nodeAtDepth;
                break;
              }
            }
            
            if (listItemNode && listItemPos !== null) {
              // Inside list - insert paragraph after video within list item
              // Use $from.after() like download/meeting rows to preserve list context
              const afterPos = videoDepth !== null ? $from.after(videoDepth) : (videoPos + videoNode.nodeSize);
              
              // Get editor from global (set by TiptapEditorComponent)
              const editor = window.currentTiptapEditor;
              if (editor) {
                editor.chain().focus().insertContentAt(afterPos, { type: 'paragraph' }).command(({ tr, dispatch }) => {
                  if (!dispatch) return false;
                  // Set selection directly to the new paragraph to preserve list context
                  const paragraphNode = tr.doc.nodeAt(afterPos);
                  if (paragraphNode && paragraphNode.type.name === 'paragraph') {
                    const resolvedPos = tr.doc.resolve(afterPos + 1);
                    tr.setSelection(TextSelection.near(resolvedPos));
                    tr.scrollIntoView();
                  }
                  return true;
                }).run();
                return true;
              }
              // Fallback: use tr.insert if editor not available
              const tr = state.tr;
              const paragraph = state.schema.nodes.paragraph.create();
              tr.insert(afterPos, paragraph);
              const resolvedPos = tr.doc.resolve(afterPos + 1);
              tr.setSelection(TextSelection.near(resolvedPos));
              tr.scrollIntoView();
              view.dispatch(tr);
              return true;
            } else {
              // Not in list - insert paragraph after video
              const afterPos = videoPos + videoNode.nodeSize;
              const tr = state.tr;
              const paragraph = state.schema.nodes.paragraph.create();
              tr.insert(afterPos, paragraph);
              const resolvedPos = tr.doc.resolve(afterPos + 1);
              tr.setSelection(TextSelection.near(resolvedPos));
              tr.scrollIntoView();
              view.dispatch(tr);
              return true;
            }
            
            return false;
          },
        },
      }),
    ];
  },

  addNodeView() {
    return ({ node, getPos, editor }) => {
      const container = document.createElement('div');
      container.className = 'video-extension-container post-content-video';
      syncIndentOnDom(container, node.attrs.indent);
      // container.style.cssText = 'margin: 0; padding: 0;';

      const video = document.createElement('video');
      video.className = 'post-content-video-vid';
      video.src = node.attrs.src;
      if (node.attrs.poster) {
        video.poster = node.attrs.poster;
      }
      video.controls = true;
      video.style.cssText = 'max-width: 100%; display: block; margin: 0; padding: 0;';
      video.draggable = false;
      video.contentEditable = false;

      // Create caption structure using shared helper
      const { wrapper: captionWrapper, container: captionContainer } = 
        createCaptionDOM('video-caption-container');

      // Setup caption keyboard handling using shared helper
      setupCaptionKeyboardHandling(captionContainer, editor);

      container.appendChild(video);
      container.appendChild(captionWrapper);
      
      // Create the menu structure directly in the node view so it persists
      // MediaNodeFloating will populate it with React components
      const menuDiv = document.createElement('div');
      menuDiv.className = postEditorVideoFixedMenuClasses();
      menuDiv.setAttribute('data-menu-slot', 'true');
      menuDiv.setAttribute('data-floating-kind', POST_EDITOR_FLOATING_MENU_KINDS.video);
      const toolbar = document.createElement('div');
      postEditorApplyFloatingToolbarElement(toolbar, POST_EDITOR_FLOATING_MENU_KINDS.video);
      const group = document.createElement('div');
      group.setAttribute('role', 'group');
      group.className = postEditorFloatingToolbarGroupClasses(POST_EDITOR_FLOATING_MENU_KINDS.video);
      group.setAttribute('data-menu-group', 'true');
      
      toolbar.appendChild(group);
      menuDiv.appendChild(toolbar);
      container.appendChild(menuDiv);
      
      // Create buttons directly in the node view so they persist
      const createButton = (icon, tooltip, onClick) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.setAttribute('data-style', 'ghost');
        btn.setAttribute('data-weight', 'small');
        btn.setAttribute('role', 'button');
        btn.setAttribute('aria-label', tooltip);
        btn.title = tooltip;
        btn.className = 'tiptap-video-fixed-menu__btn';
        btn.innerHTML = icon;
        btn.onclick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          onClick(e);
        };
        return btn;
      };
      
      // Create separator
      const createSeparator = () => {
        const sep = document.createElement('div');
        sep.setAttribute('role', 'separator');
        sep.setAttribute('aria-orientation', 'vertical');
        sep.className = 'tiptap-video-fixed-menu__separator';
        return sep;
      };
      
      // SVG icons (simple inline SVGs) - matching drag context menu icons
      const captionIcon = '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M7 22C7 21.4477 7.44772 21 8 21H16C16.5523 21 17 21.4477 17 22C17 22.5523 16.5523 23 16 23H8C7.44772 23 7 22.5523 7 22Z" fill="currentColor"/><path fill-rule="evenodd" clip-rule="evenodd" d="M3 18C3 17.4477 3.44772 17 4 17H20C20.5523 17 21 17.4477 21 18C21 18.5523 20.5523 19 20 19H4C3.44772 19 3 18.5523 3 18Z" fill="currentColor"/><path fill-rule="evenodd" clip-rule="evenodd" d="M3 5C3 3.34315 4.34315 2 6 2H18C19.6569 2 21 3.34315 21 5V11C21 12.6569 19.6569 14 18 14H6C4.34315 14 3 12.6569 3 11V5ZM6 4C5.44772 4 5 4.44772 5 5V11C5 11.5523 5.44772 12 6 12H18C18.5523 12 19 11.5523 19 11V5C19 4.44772 18.5523 4 18 4H6Z" fill="currentColor"/></svg>';
      const downloadIcon = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>';
      const replaceIcon = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M3 21v-5h5"/></svg>';
      const uploadThumbnailIcon = '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M20 2C20 1.44772 19.5523 1 19 1C18.4477 1 18 1.44772 18 2V4H16C15.4477 4 15 4.44772 15 5C15 5.55228 15.4477 6 16 6H18V8C18 8.55228 18.4477 9 19 9C19.5523 9 20 8.55228 20 8V6H22C22.5523 6 23 5.55228 23 5C23 4.44772 22.5523 4 22 4H20V2ZM5 4C4.73478 4 4.48043 4.10536 4.29289 4.29289C4.10536 4.48043 4 4.73478 4 5V19C4 19.2652 4.10536 19.5196 4.29289 19.7071C4.48043 19.8946 4.73478 20 5 20H5.58579L14.379 11.2068C14.9416 10.6444 15.7045 10.3284 16.5 10.3284C17.2955 10.3284 18.0584 10.6444 18.621 11.2068L20 12.5858V12C20 11.4477 20.4477 11 21 11C21.5523 11 22 11.4477 22 12V14.998C22 14.9994 22 15.0007 22 15.002V19C22 19.7957 21.6839 20.5587 21.1213 21.1213C20.5587 21.6839 19.7957 22 19 22H6.00219C6.00073 22 5.99927 22 5.99781 22H5C4.20435 22 3.44129 21.6839 2.87868 21.1213C2.31607 20.5587 2 19.7957 2 19V5C2 4.20435 2.31607 3.44129 2.87868 2.87868C3.44129 2.31607 4.20435 2 5 2H12C12.5523 2 13 2.44772 13 3C13 3.55228 12.5523 4 12 4H5ZM8.41422 20H19C19.2652 20 19.5196 19.8946 19.7071 19.7071C19.8946 19.5196 20 19.2652 20 19V15.4142L17.207 12.6212C17.0195 12.4338 16.7651 12.3284 16.5 12.3284C16.2349 12.3284 15.9806 12.4337 15.7931 12.6211L8.41422 20ZM6.87868 6.87868C7.44129 6.31607 8.20435 6 9 6C9.79565 6 10.5587 6.31607 11.1213 6.87868C11.6839 7.44129 12 8.20435 12 9C12 9.79565 11.6839 10.5587 11.1213 11.1213C10.5587 11.6839 9.79565 12 9 12C8.20435 12 7.44129 11.6839 6.87868 11.1213C6.31607 10.5587 6 9.79565 6 9C6 8.20435 6.31607 7.44129 6.87868 6.87868ZM9 8C8.73478 8 8.48043 8.10536 8.29289 8.29289C8.10536 8.48043 8 8.73478 8 9C8 9.26522 8.10536 9.51957 8.29289 9.70711C8.48043 9.89464 8.73478 10 9 10C9.26522 10 9.51957 9.89464 9.70711 9.70711C9.89464 9.51957 10 9.26522 10 9C10 8.73478 9.89464 8.48043 9.70711 8.29289C9.51957 8.10536 9.26522 8 9 8Z" fill="currentColor"/></svg>';
      const deleteIcon = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>';
      const tagIcon = '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M20.59 13.41 11 3.83A2 2 0 0 0 9.59 3H4a1 1 0 0 0-1 1v5.59A2 2 0 0 0 3.83 11l9.58 9.59a2 2 0 0 0 2.83 0l4.35-4.35a2 2 0 0 0 0-2.83ZM6.5 7.5A1 1 0 1 1 7.5 6.5 1 1 0 0 1 6.5 7.5Z"/></svg>';
      
      // Create buttons with event handlers
      const captionBtn = createButton(captionIcon, 'Toggle Caption', (e) => {
        const pos = getPos();
        if (typeof pos === 'number' && pos >= 0) {
          editor.chain().focus().setNodeSelection(pos).run();
          setTimeout(() => {
            // Dispatch event for caption toggle - MediaNodeFloating will handle it
            const ev = new CustomEvent('tiptap:video:toggleCaption', {
              bubbles: true,
              detail: { nodePos: pos }
            });
            container.dispatchEvent(ev);
          }, 10);
        }
      });
      
      const downloadBtn = createButton(downloadIcon, 'Download', (e) => {
        const pos = getPos();
        if (typeof pos === 'number' && pos >= 0) {
          editor.chain().focus().setNodeSelection(pos).run();
          setTimeout(() => {
            // Dispatch event for download - MediaNodeFloating will handle it
            const ev = new CustomEvent('tiptap:video:download', {
              bubbles: true,
              detail: { nodePos: pos }
            });
            container.dispatchEvent(ev);
          }, 10);
        }
      });

      const tagsBtn = createButton(tagIcon, 'Tags', () => {
        const pos = getPos();
        if (typeof pos !== 'number' || pos < 0) return;
        const current = editor.state.doc.nodeAt(pos);
        let rid = current?.attrs?.resourceId;
        if (!rid) {
          const nodeDOM = editor.view.nodeDOM(pos);
          const withId = nodeDOM?.closest?.('[data-resource-id]');
          rid = withId?.getAttribute('data-resource-id');
        }
        if (!rid) return;
        document.dispatchEvent(new CustomEvent('tiptap:resource:requestTags', {
          bubbles: true,
          cancelable: true,
          detail: { resourceId: String(rid) },
        }));
      });
      
      const replaceBtn = createButton(replaceIcon, 'Replace Video', (e) => {
        const pos = getPos();
        if (typeof pos === 'number' && pos >= 0) {
          const nodeDOM = editor.view.nodeDOM(pos);
          const ev = new CustomEvent('tiptap:video:requestReplace', {
            bubbles: true,
            cancelable: true,
            detail: {
              nodePos: pos,
              resourceId: node.attrs.resourceId || '',
              src: node.attrs.src || '',
            }
          });
          if (nodeDOM) {
            nodeDOM.dispatchEvent(ev);
          }
          if (!ev.defaultPrevented) {
            document.dispatchEvent(ev);
          }
        }
      });

      const uploadThumbnailBtn = createButton(uploadThumbnailIcon, 'Upload thumbnail', (e) => {
        const resourceId = node.attrs.resourceId;
        if (resourceId) {
          document.dispatchEvent(new CustomEvent('tiptap:video:uploadThumbnail', {
            bubbles: true,
            detail: { resourceId: String(resourceId) }
          }));
        }
      });
      
      const deleteBtn = createButton(deleteIcon, 'Delete', (e) => {
        const pos = getPos();
        if (typeof pos === 'number' && pos >= 0) {
          editor.chain().focus().setNodeSelection(pos).deleteSelection().run();
        }
      });
      
      // Append buttons to group
      group.appendChild(captionBtn);
      group.appendChild(createSeparator());
      group.appendChild(downloadBtn);
      group.appendChild(createSeparator());
      group.appendChild(tagsBtn);
      group.appendChild(createSeparator());
      group.appendChild(replaceBtn);
      group.appendChild(createSeparator());
      group.appendChild(uploadThumbnailBtn);
      group.appendChild(createSeparator());
      group.appendChild(deleteBtn);
      
      // Store references for MediaNodeFloating to use (if needed)
      container._menuSlot = menuDiv;
      container._menuGroup = group;
      
      // Hide menu when ResourceModal is open
      const checkModalAndUpdateMenu = () => {
        const modalOverlay = document.querySelector('.resource-modal-overlay');
        const modalIsOpen = !!modalOverlay;
        if (modalIsOpen) {
          menuDiv.style.display = 'none';
        } else {
          menuDiv.style.display = 'flex';
        }
      };
      
      // Listen for replacement events that open the modal
      const handleModalOpen = () => {
        menuDiv.style.display = 'none';
      };
      
      document.addEventListener('tiptap:video:requestReplace', handleModalOpen);
      document.addEventListener('tiptap:image:requestReplace', handleModalOpen);
      document.addEventListener('tiptap:downloadRow:requestReplace', handleModalOpen);
      document.addEventListener('tiptap:externalLinkRow:requestReplace', handleModalOpen);
      
      // Check modal state periodically
      const modalCheckInterval = setInterval(checkModalAndUpdateMenu, 100);
      
      // Watch for DOM changes to detect modal open/close
      const modalObserver = new MutationObserver(checkModalAndUpdateMenu);
      modalObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
      
      // Initial check
      checkModalAndUpdateMenu();

      return {
        dom: container,
        contentDOM: captionContainer,
        update: (updatedNode) => {
          if (updatedNode.type.name !== this.name) return false;
          syncIndentOnDom(container, updatedNode.attrs.indent);
          video.src = updatedNode.attrs.src;
          if (updatedNode.attrs.poster) {
            video.poster = updatedNode.attrs.poster;
          } else {
            video.removeAttribute('poster');
          }
          // Ensure menu slot still exists and has buttons
          let menuSlot = container.querySelector('.tiptap-video-fixed-menu[data-menu-slot="true"]');
          if (!menuSlot) {
            // Recreate if somehow lost
            menuSlot = document.createElement('div');
            menuSlot.className = postEditorVideoFixedMenuClasses();
            menuSlot.setAttribute('data-menu-slot', 'true');
            menuSlot.setAttribute('data-floating-kind', POST_EDITOR_FLOATING_MENU_KINDS.video);
            const toolbar = document.createElement('div');
            postEditorApplyFloatingToolbarElement(toolbar, POST_EDITOR_FLOATING_MENU_KINDS.video);
            const group = document.createElement('div');
            group.setAttribute('role', 'group');
            group.className = postEditorFloatingToolbarGroupClasses(POST_EDITOR_FLOATING_MENU_KINDS.video);
            group.setAttribute('data-menu-group', 'true');
            toolbar.appendChild(group);
            menuSlot.appendChild(toolbar);
            container.appendChild(menuSlot);
            container._menuSlot = menuSlot;
            container._menuGroup = group;
          }
          
          // Repopulate if group is empty or missing buttons added after initial deploy
          const group = menuSlot.querySelector('.tiptap-toolbar-group[data-menu-group="true"]');
          const buttonLabels = () => Array.from(group.querySelectorAll('button')).map(b => (b.title || b.getAttribute('aria-label') || ''));
          const hasUploadThumbnail = group && buttonLabels().includes('Upload thumbnail');
          const hasTags = group && buttonLabels().includes('Tags');
          if (group && (group.children.length === 0 || !hasUploadThumbnail || !hasTags)) {
            if (group.children.length > 0) {
              group.innerHTML = '';
            }
            // Reuse the button creation logic from addNodeView
            const createButton = (icon, tooltip, onClick) => {
              const btn = document.createElement('button');
              btn.type = 'button';
              btn.setAttribute('data-style', 'ghost');
              btn.setAttribute('data-weight', 'small');
              btn.setAttribute('role', 'button');
              btn.setAttribute('aria-label', tooltip);
              btn.title = tooltip;
              btn.className = 'tiptap-video-fixed-menu__btn';
              btn.innerHTML = icon;
              btn.onclick = (e) => {
                e.preventDefault();
                e.stopPropagation();
                onClick(e);
              };
              return btn;
            };
            
            const createSeparator = () => {
              const sep = document.createElement('div');
              sep.setAttribute('role', 'separator');
              sep.setAttribute('aria-orientation', 'vertical');
              sep.className = 'tiptap-video-fixed-menu__separator';
              return sep;
            };
            
            const captionIcon = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>';
            const downloadIcon = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>';
            const replaceIcon = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M3 21v-5h5"/></svg>';
            const uploadThumbnailIcon = '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M20 2C20 1.44772 19.5523 1 19 1C18.4477 1 18 1.44772 18 2V4H16C15.4477 4 15 4.44772 15 5C15 5.55228 15.4477 6 16 6H18V8C18 8.55228 18.4477 9 19 9C19.5523 9 20 8.55228 20 8V6H22C22.5523 6 23 5.55228 23 5C23 4.44772 22.5523 4 22 4H20V2ZM5 4C4.73478 4 4.48043 4.10536 4.29289 4.29289C4.10536 4.48043 4 4.73478 4 5V19C4 19.2652 4.10536 19.5196 4.29289 19.7071C4.48043 19.8946 4.73478 20 5 20H5.58579L14.379 11.2068C14.9416 10.6444 15.7045 10.3284 16.5 10.3284C17.2955 10.3284 18.0584 10.6444 18.621 11.2068L20 12.5858V12C20 11.4477 20.4477 11 21 11C21.5523 11 22 11.4477 22 12V14.998C22 14.9994 22 15.0007 22 15.002V19C22 19.7957 21.6839 20.5587 21.1213 21.1213C20.5587 21.6839 19.7957 22 19 22H6.00219C6.00073 22 5.99927 22 5.99781 22H5C4.20435 22 3.44129 21.6839 2.87868 21.1213C2.31607 20.5587 2 19.7957 2 19V5C2 4.20435 2.31607 3.44129 2.87868 2.87868C3.44129 2.31607 4.20435 2 5 2H12C12.5523 2 13 2.44772 13 3C13 3.55228 12.5523 4 12 4H5ZM8.41422 20H19C19.2652 20 19.5196 19.8946 19.7071 19.7071C19.8946 19.5196 20 19.2652 20 19V15.4142L17.207 12.6212C17.0195 12.4338 16.7651 12.3284 16.5 12.3284C16.2349 12.3284 15.9806 12.4337 15.7931 12.6211L8.41422 20ZM6.87868 6.87868C7.44129 6.31607 8.20435 6 9 6C9.79565 6 10.5587 6.31607 11.1213 6.87868C11.6839 7.44129 12 8.20435 12 9C12 9.79565 11.6839 10.5587 11.1213 11.1213C10.5587 11.6839 9.79565 12 9 12C8.20435 12 7.44129 11.6839 6.87868 11.1213C6.31607 10.5587 6 9.79565 6 9C6 8.20435 6.31607 7.44129 6.87868 6.87868ZM9 8C8.73478 8 8.48043 8.10536 8.29289 8.29289C8.10536 8.48043 8 8.73478 8 9C8 9.26522 8.10536 9.51957 8.29289 9.70711C8.48043 9.89464 8.73478 10 9 10C9.26522 10 9.51957 9.89464 9.70711 9.70711C9.89464 9.51957 10 9.26522 10 9C10 8.73478 9.89464 8.48043 9.70711 8.29289C9.51957 8.10536 9.26522 8 9 8Z" fill="currentColor"/></svg>';
            const deleteIcon = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>';
            const tagIcon = '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M20.59 13.41 11 3.83A2 2 0 0 0 9.59 3H4a1 1 0 0 0-1 1v5.59A2 2 0 0 0 3.83 11l9.58 9.59a2 2 0 0 0 2.83 0l4.35-4.35a2 2 0 0 0 0-2.83ZM6.5 7.5A1 1 0 1 1 7.5 6.5 1 1 0 0 1 6.5 7.5Z"/></svg>';
            
            const pos = getPos();
            const captionBtn = createButton(captionIcon, 'Toggle Caption', (e) => {
              if (typeof pos === 'number' && pos >= 0) {
                editor.chain().focus().setNodeSelection(pos).run();
                setTimeout(() => {
                  const ev = new CustomEvent('tiptap:video:toggleCaption', {
                    bubbles: true,
                    detail: { nodePos: pos }
                  });
                  container.dispatchEvent(ev);
                }, 10);
              }
            });
            
            const downloadBtn = createButton(downloadIcon, 'Download', (e) => {
              if (typeof pos === 'number' && pos >= 0) {
                editor.chain().focus().setNodeSelection(pos).run();
                setTimeout(() => {
                  const ev = new CustomEvent('tiptap:video:download', {
                    bubbles: true,
                    detail: { nodePos: pos }
                  });
                  container.dispatchEvent(ev);
                }, 10);
              }
            });

            const tagsBtn = createButton(tagIcon, 'Tags', () => {
              if (typeof pos !== 'number' || pos < 0) return;
              const current = editor.state.doc.nodeAt(pos);
              let rid = current?.attrs?.resourceId;
              if (!rid) {
                const nodeDOM = editor.view.nodeDOM(pos);
                const withId = nodeDOM?.closest?.('[data-resource-id]');
                rid = withId?.getAttribute('data-resource-id');
              }
              if (!rid) rid = updatedNode.attrs.resourceId;
              if (!rid) return;
              document.dispatchEvent(new CustomEvent('tiptap:resource:requestTags', {
                bubbles: true,
                cancelable: true,
                detail: { resourceId: String(rid) },
              }));
            });
            
            const replaceBtn = createButton(replaceIcon, 'Replace Video', (e) => {
              if (typeof pos === 'number' && pos >= 0) {
                const nodeDOM = editor.view.nodeDOM(pos);
                const ev = new CustomEvent('tiptap:video:requestReplace', {
                  bubbles: true,
                  cancelable: true,
                  detail: {
                    nodePos: pos,
                    resourceId: updatedNode.attrs.resourceId || '',
                    src: updatedNode.attrs.src || '',
                  }
                });
                if (nodeDOM) {
                  nodeDOM.dispatchEvent(ev);
                }
                if (!ev.defaultPrevented) {
                  document.dispatchEvent(ev);
                }
              }
            });
            
            const uploadThumbnailBtn = createButton(uploadThumbnailIcon, 'Upload thumbnail', (e) => {
              const resourceId = updatedNode.attrs.resourceId;
              if (resourceId) {
                document.dispatchEvent(new CustomEvent('tiptap:video:uploadThumbnail', {
                  bubbles: true,
                  detail: { resourceId: String(resourceId) }
                }));
              }
            });
            
            const deleteBtn = createButton(deleteIcon, 'Delete', (e) => {
              if (typeof pos === 'number' && pos >= 0) {
                editor.chain().focus().setNodeSelection(pos).deleteSelection().run();
              }
            });
            
            group.appendChild(captionBtn);
            group.appendChild(createSeparator());
            group.appendChild(downloadBtn);
            group.appendChild(createSeparator());
            group.appendChild(tagsBtn);
            group.appendChild(createSeparator());
            group.appendChild(replaceBtn);
            group.appendChild(createSeparator());
            group.appendChild(uploadThumbnailBtn);
            group.appendChild(createSeparator());
            group.appendChild(deleteBtn);
          }
          return true;
        },
        destroy() {
          // Clean up event listeners and observers
          document.removeEventListener('tiptap:video:requestReplace', handleModalOpen);
          document.removeEventListener('tiptap:image:requestReplace', handleModalOpen);
          document.removeEventListener('tiptap:downloadRow:requestReplace', handleModalOpen);
          document.removeEventListener('tiptap:externalLinkRow:requestReplace', handleModalOpen);
          clearInterval(modalCheckInterval);
          modalObserver.disconnect();
          container.remove();
        }
      };
    };
  },
});
